<template>
  <div class="fixed inset-0 bg-black bg-opacity-50 z-30 flex">
    <div class="fixed inset-0" @click="setQuickViewProduct(null)"/>
    <div class="bg-white w-11/12 xl:w-8/12 m-auto align-middle p-2">
      <div class="relative p-4">
        <i
          class="material-icons absolute right-0 top-0 cursor-pointer"
          @click="setQuickViewProduct(null)"
        >
          close
        </i>
        <div v-if="loaded" class="flex py-2">
          <div class="w-2/5 p-16">
            <ItemCarousel :items="product.images" />
          </div>
          <div class="w-3/5">
            <h3 class="text-2xl leading-tight">{{ product.name }}</h3>
            <div class="md:flex py-2 border-b">
              <div class="pr-4">
                <span>Brand:</span>
                <span class="pl-4 text-blue-600 capitalize">
                  {{ product.brand }}
                </span>
              </div>
              <div class="text-gray-450 hidden md:block">|</div>
              <div class="md:pl-4">
                <span>Model No:</span>
                <span class="pl-4 capitalize">
                  {{ product.sku }}
                </span>
              </div>
            </div>
            <div class="py-4 border-b">
              <div class="mb-2">
                <span class="text-danger ml-3 text-2xl font-bold">
                  {{ product.price }}
                </span>
              </div>
              <div v-if="product.stockInUsa" class="flex items-center pb-2">
                <img src="@/assets/icons/usa.svg" class="w-8">
                <span class="ml-2 pt-1">Stock in USA</span>
              </div>
              <div class="mb-4">
                <span>Sold By:
                  <router-link
                    :to="`/suppliers/${product.supplier.id}`"
                    class="font-bold text-blue-600"
                  >
                    {{ product.supplier.name }}
                  </router-link>
                </span>
              </div>
              <div class="pb-4">
                <p>
                  <span class="font-bold">Category: </span>
                  {{product.categoryName}}
                </p>
              </div>
              <ul class="text-gray-700">
                <li class="pb-2">
                  {{ product.shortDescription }}
                </li>
              </ul>
              <div>
                MOQ: {{ product.displayMoq }}
              </div>
            </div>
            <div class="py-4">
              <span>
                <span class="text-xl font-bold">Price: </span>
                <span class="font-bold">{{ productTotal }}</span>
              </span>
              <div class="md:flex relative">
                <div>
                  <p>Quantity</p>
                  <QuantityChanger
                    :product="product"
                    :quantity="quantity"
                    @setQuantity="changeQuantity"
                    @changeQuantity="changeQuantity"
                  />
                </div>
                <div class="md:px-2 mt-auto">
                  <p>Inco Terms</p>
                  <div class="pl-2 pr-1 border">
                    <select
                      v-model="incoterm"
                      class="w-full h-12 bg-white focus:outline-none"
                      @change="updatePrice"
                    >
                      <option
                        v-for="(option, index) in product.incoTerms"
                        :key="`option-${index}`"
                        :value="option"
                        :selected="index === 0"
                      >
                        {{ option }}
                      </option>
                    </select>
                  </div>
                </div>
                <div class="text-center mt-auto">
                  <button
                    @click="addToCart"
                    :disabled="illegalQuantity"
                    class="app-btn mx-2 mt-2"
                    :class="{'opacity-25': illegalQuantity}"
                  >
                    Add to Cart
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div v-else class="flex items-center justify-center py-4 text-center min-h-70v">
          Loading...
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapGetters, mapActions } from 'vuex'
import ItemCarousel from '@/shared/components/product/ItemCarousel'
import QuantityChanger from '@/shared/components/product/productInfo/QuantityChanger'

export default {
  name: 'ProductQuickView',
  components: {
    ItemCarousel,
    QuantityChanger
  },
  data() {
    return {
      quantity: 1,
      value: 0,
      product: {},
      loaded: false,
      moq: 0,
      incoterm: '',
      productTotal: ''
    }
  },
  computed: {
    ...mapGetters ([
      'getQuicKViewProduct'
    ]),
    illegalQuantity() {
      return this.quantity < this.moq
    }
  },
  async created() {
    await this.getProductData()
  },
  methods: {
    ...mapActions ([
      'setQuickViewProduct',
      'getProductInfo',
      'addItemToCart'
    ]),
    async getProductData() {
      this.product = await this.getProductInfo(this.getQuicKViewProduct)
      if (!this.product.result) {
        this.$notify({
          group: 'buyHive',
          type: 'error',
          text: this.product.message
        })
        this.setQuickViewProduct(null)
        return
      }
      if (this.product.incoTerms.includes('FOB')) {
        const index = this.product.incoTerms.indexOf('FOB')
        this.product.incoTerms.splice(index, 1)
        this.product.incoTerms.unshift('FOB')
      }
      this.loaded = true
      this.moq = this.product.minimumOrderQuantity
      this.quantity = this.moq
      this.incoterm = this.product.incoTerms[0]
      this.updatePrice()
    },
    changeQuantity(val) {
      this.quantity = val
      this.updatePrice()
    },
    updatePrice() {
      const unit = this.product.unit
      this.product.pricing.some((priceRange) => {
        const quantityMax = String(priceRange.maxQuantity).replace(/,/g, "")
        if(this.quantity <= parseInt(quantityMax)){
          if(this.incoterm == 'EXW')
            this.productTotal = `${priceRange.displayExwPrice} / ${unit}`
          else if(this.incoterm == 'FOB')
            this.productTotal = `${priceRange.displayFobPrice} / ${unit}`
          return true
        }
        else{
          if(this.incoterm == 'EXW')
            this.productTotal = `$ ${this.product.lowestExwPrice} / ${unit}`
          else if(this.incoterm == 'FOB')
            this.productTotal = `$ ${this.product.lowestFobPrice} / ${unit}`
        }
      })
    },
    addToCart() {
      const item = {...this.product}
      item.inco_terms = this.incoterm
      item.quantity = this.quantity
      this.addItemToCart(item)
      this.$notify({
        group: 'buyHive',
        type: 'success',
        text: 'Product Added To Cart'
      });
    }
  }
}
</script>
